import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { UserProfile } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
  apiUrl = environment.apiUrl;
  httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  private currentUser: UserProfile | null = null;
  private loadInProgress: Observable<UserProfile | null> | null = null;
  private generation = 0;

  constructor(private httpClient: HttpClient) {}

  ensureLoaded(): Observable<UserProfile | null> {
    if (this.currentUser) {
      return of(this.currentUser);
    }
    if (!this.loadInProgress) {
      const requestGeneration = this.generation;
      this.loadInProgress = this.httpClient.get<UserProfile>(`${this.apiUrl}/auth/me`, this.httpOptions).pipe(
        tap((user) => {
          if (requestGeneration === this.generation) {
            this.currentUser = user;
          }
        }),
        catchError(() => {
          if (requestGeneration === this.generation) {
            this.loadInProgress = null;
          }
          return of(null);
        }),
        shareReplay(1)
      );
    }
    return this.loadInProgress;
  }

  isAdmin(): boolean {
    return this.currentUser?.roles?.includes('ROLE_ADMIN') ?? false;
  }

  hasPrivilege(code: string): boolean {
    if (this.isAdmin()) {
      return true;
    }
    return this.currentUser?.privileges?.includes(code) ?? false;
  }

  hasAnyPrivilege(codes: string[]): boolean {
    if (!codes?.length) {
      return true;
    }
    if (this.isAdmin()) {
      return true;
    }
    return codes.some((code) => this.currentUser?.privileges?.includes(code) ?? false);
  }

  hasAllPrivileges(codes: string[]): boolean {
    if (!codes?.length) {
      return true;
    }
    if (this.isAdmin()) {
      return true;
    }
    return codes.every((code) => this.currentUser?.privileges?.includes(code) ?? false);
  }

  /**
   * Privileges actually granted, without the ROLE_ADMIN shortcut: the API only trusts the
   * privileges attached to the user's roles, so actions it enforces strictly must match it.
   */
  holdsAllPrivileges(codes: string[]): boolean {
    return codes.every((code) => this.currentUser?.privileges?.includes(code) ?? false);
  }

  isClientUser(): boolean {
    return this.currentUser?.clientId != null;
  }

  isOnboardingOnlyUser(): boolean {
    if (!this.currentUser?.roles?.includes('CLIENT_ONBOARDING')) {
      return false;
    }

    const operationalPrivileges = [
      'TRANSACTION_VIEW',
      'CLIENT_CREDENTIAL_VIEW',
      'CLIENT_SECRET_REGENERATE',
      'FUNDING_REQUEST_VIEW',
      'CLIENT_SETTINGS_VIEW'
    ];
    return !operationalPrivileges.some((privilege) => this.currentUser?.privileges?.includes(privilege));
  }

  clear(): void {
    this.generation++;
    this.currentUser = null;
    this.loadInProgress = null;
  }
}
